import { Box, Flex, Image, Text, Wrap } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import type { Companion } from "src/models/companion";
import { useChatStore } from "src/stores/chatStoreContext";
import { withAlpha } from "src/theme";
import { tap } from "src/utils/haptic";
import { BottomSheet } from "./BottomSheet";
import { GradientButton } from "./GradientButton";
import { ThemeBadge } from "./ThemeBadge";

/** The companion's profile, as a sheet: picture, tagline, bio, traits, start. */
export const CompanionSheet = observer(
  ({
    companion,
    onClose,
    onStart,
  }: {
    companion: Companion | null;
    onClose: () => void;
    /** Instead of opening the chat (the sheet is shown inside it). */
    onStart?: () => void;
  }) => {
    const navigate = useNavigate();
    const chatStore = useChatStore();
    const hasChat = companion ? chatStore.hasChat(companion.id) : false;

    return (
      <BottomSheet open={companion !== null} onClose={onClose}>
        {companion && (
          <>
            <Box
              position="relative"
              aspectRatio="1 / 1"
              bg={withAlpha(companion.color, 0.3)}
            >
              {companion.avatarURL && (
                <Image
                  src={companion.avatarURL}
                  alt={companion.name}
                  boxSize="100%"
                  objectFit="cover"
                  draggable={false}
                />
              )}
              <Box
                position="absolute"
                inset={0}
                bg="linear-gradient(180deg, rgba(25,19,19,0) 50%, #191313 100%)"
              />
              <Box position="absolute" top={5} left={4}>
                <ThemeBadge theme={companion.theme} />
              </Box>
            </Box>

            <Box px={5} mt={-14} position="relative">
              <Text
                fontSize="32px"
                fontWeight="bold"
                letterSpacing="-0.02em"
                lineHeight="1.1"
              >
                {companion.name}
              </Text>
              <Text color="muted" mt={1}>
                {companion.tagline}
              </Text>

              <Text fontSize="sm" color="muted" mt={5} lineHeight="1.6">
                {companion.bio}
              </Text>

              <Wrap gap={2} mt={4}>
                {[...companion.traits, ...companion.interests].map((t) => (
                  <Text
                    key={t}
                    as="span"
                    fontSize="xs"
                    px={3}
                    py={1.5}
                    borderRadius="full"
                    bg="elevated"
                    color="rgba(255,255,255,0.85)"
                  >
                    {t}
                  </Text>
                ))}
              </Wrap>
            </Box>

            {/* Sticky, so "Start" is always in reach while reading. */}
            <Box
              position="sticky"
              bottom={0}
              px={5}
              pt={6}
              pb={4}
              bg="linear-gradient(180deg, rgba(25,19,19,0) 0%, #191313 35%)"
            >
              <Flex>
                <GradientButton
                  onClick={() => {
                    tap();
                    if (onStart) onStart();
                    else navigate(`/chat/${companion.id}`);
                  }}
                >
                  {hasChat
                    ? `Continue with ${companion.name}`
                    : `Start chatting`}
                </GradientButton>
              </Flex>
              <Text fontSize="11px" color="faint" textAlign="center" mt={2}>
                {companion.name} is an AI companion, not a real person.
              </Text>
            </Box>
          </>
        )}
      </BottomSheet>
    );
  },
);
